/**
 * @fileoverview 500 Server Error page.
 * @description Shown when a request fails on the backend. Composes the auth page shell with the
 * shared error-state card, plus a "Try Again" action that returns to the page whose request
 * failed. The Axios response interceptor pushes /500 on top of that page, so going back one entry
 * remounts it and re-runs its requests — reloading would only reload /500 itself. When /500 is the
 * first entry in this tab's history (opened directly or from a bookmark), there's no page to go
 * back to, so "Try Again" falls back to the visitor's home route instead.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import serverErrorIcon from '@/assets/svg/errors/icon-server-error.svg'
import AuthLayout from '@/shared/components/layouts/AuthLayout'
import ErrorStateCard from '@/shared/components/molecules/ErrorStateCard'
import { resolveHomeRoute } from '@/shared/lib/auth/resolve-home-route'
import { useAuth } from '@/shared/lib/auth/useAuth'

/**
 * @component
 */
function ServerErrorPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()

  /**
   * @description Goes back to the page that failed, or home when there's no earlier entry — React
   * Router gives the first location in a history stack the key "default".
   */
  function retry() {
    if (location.key === 'default') {
      void navigate(resolveHomeRoute(user?.privileges ?? []), { replace: true })
    } else {
      void navigate(-1)
    }
  }

  return (
    <AuthLayout>
      <ErrorStateCard
        statusCode="500"
        icon={serverErrorIcon}
        accent="error"
        title={t('errors.serverError.title')}
        description={t('errors.serverError.description')}
        showRetry
        onRetry={retry}
      />
    </AuthLayout>
  )
}

export default ServerErrorPage
